import _ from 'lodash'
import {MutationTree} from 'vuex'
import {ShippingState} from '@/modules/shipping/store'
import Carrier from '@/modules/shipping/model/Carrier'
import Rate from '@/modules/shipping/model/Rate'
import Region from '@/modules/shipping/model/Region'
import RegionDetailMode from '@/modules/shipping/model/RegionDetailMode'
import Weight from '@/modules/shipping/model/Weight'
import RateDetailMode from '@/modules/shipping/model/RateDetailMode'

export enum MutationTypes {
    SetCurrentRate = 'setCurrentRate',
    SetCurrentRegion = 'setCurrentRegion',
    SetRegionDetailMode = 'setRegionDetailMode',
    SetNewRegionToCurrent = 'setNewRegionToCurrent',
    AddRegion = 'region/add',
    SetWeight = 'region/weight/set',
    SetRateList = 'rate/list/set',
    SetRateListLoading = 'rate/list/loading/set',
    SetDeletingRate = 'rate/deleting/set',
    SetRateToDelete = 'rate/to-delete/set',
    RemoveRate = 'rate/remove',
    UpdateCurrentRateField = 'rate/current/field/update',

    // Mutaciones para regiones
    UpdateRegion = 'region/update',
    UpdateRegions = 'regions/update',
    SetRateDetailMode = 'rate-detail/mode/set',
    ReplaceRegions = 'regions/replace',
    RemoveRateRegion = 'rate/current/region/remove',
    RemoveWeightInAllRegions = 'REMOVE_WEIGHT_IN_ALL_REGIONS',
    UpdateRegionField = 'UPDATE_REGION_FIELD',

    // Mutaciones para la edición del transportista
    SetCurrentCarrier = 'SET_CURRENT_CARRIER',
    SetCarrierList = 'setCarrierList',
    SetCarrierListFullyLoaded = 'setCarrierListFullyLoaded',
    UpdateCarrierField = 'UPDATE_CARRIER_FIELD',
    AddCarrier = 'ADD_CARRIER',
    DeleteCarrier = 'DELETE_CARRIER',
}

/**
 * Buscar región
 */
const findRegion = (state: ShippingState, region: Region): Region | undefined => {
    if (region.tempId !== '') {
        return state.regions.find(r => r.tempId === region.tempId)
    }
    return state.regions.find(r => r.id === region.id)

}

/**
 * Buscar el indice de una región
 *
 * @param state
 * @param region
 */
const findRegionIndex = (state: ShippingState, region: Region): number => {
    if (region.tempId === '' || region.id !== 0) {
        return state.regions.findIndex(r => r.id === region.id)
    }
    return state.regions.findIndex(r => r.tempId === region.tempId)
}

/**
 * @param price
 */
const parsePrice = (price: any): number => {
    if (!price) {
        return 0
    }

    const type = typeof price
    if (type === 'number') {
        return price
    }

    if (type === 'string') {
        const float = parseFloat(price)
        return isNaN(float) ? 0 : float
    }

    throw new Error(`El precio a convertir debe ser un string o numero, se obtuvo el tipo ${type}`)
}

const mutations: MutationTree<ShippingState> = {
    [MutationTypes.SetCarrierList]: (state, carriers: Carrier[]) => state.carriers = carriers,
    [MutationTypes.SetCarrierListFullyLoaded]: (state, loaded: boolean) => state.carrierListFullyLoaded = loaded,
    [MutationTypes.SetCurrentRate]: (state, rate: Rate) => state.currentRate = rate,
    /**
     * Establece la región actualmente en edición por el usuario
     *
     * @param state
     * @param region
     */
    [MutationTypes.SetCurrentRegion]: (state, region: Region) => state.currentRegion = _.cloneDeep(region),
    [MutationTypes.SetRegionDetailMode]: (state, mode: RegionDetailMode) => state.regionDetailMode = mode,

    /**
     * Crea una nueva región y la inserta como región actualmente en vista/edición
     */
    [MutationTypes.SetNewRegionToCurrent]: (state, rate: Rate) => {
        const region = new Region()
        region.rate = rate
        state.currentRegion = region
    },

    /**
     * Añade una región
     *
     * Si la región ya existe se reemplazará por la nueva
     */
    [MutationTypes.AddRegion]: (state, region: Region) => {
        state.regions.forEach((r: Region) => {
            if (r.id === region.id) {
                region.id = region.id + 1
            }
        })
        state.regions.push(region)
    },

    /**
     * Registra un peso en una región
     */
    [MutationTypes.SetWeight]: (state, payload: { region: Region, weight: number, price: number }) => {
        const region = findRegion(state, payload.region)
        if (!region) {
            throw new Error(`La región no existe ${payload.region.id || payload.region.tempId}`)
        }

        const weight = region.weights.find(w => w.weight === payload.weight)
        if (weight) {
            weight.price = parsePrice(payload.price)
            return
        }

        region.weights.push(new Weight(payload.weight, parsePrice(payload.price)))
    },

    /**
     * Establece la lista de tarifas
     */
    [MutationTypes.SetRateList]: (state, rates: Rate[]) => {
        state.rates = rates
    },

    /**
     * Establece si se encuentra en ejecución el proceso para obtener la lista
     * de tarifas del servidor
     */
    [MutationTypes.SetRateListLoading]: (state, loading: boolean) => {
        state.loadingRates = loading
    },

    /**
     * Establece si se encuentra en ejecución el proceso para eliminar una tarifa
     * de la aplicación
     */
    [MutationTypes.SetDeletingRate]: (state, deleting: boolean) => state.deletingRate = deleting,
    [MutationTypes.SetRateToDelete]: (state, rate: Rate) => state.rateToDelete = rate,

    /**
     * Elimina un tarifa de la lista de tarifas
     */
    [MutationTypes.RemoveRate]: (state, rate: Rate | number) => {
        const index = state.rates.findIndex(r => r.id === rate)
        if (index < 0) {
            throw new Error('No se puede eliminar la tarifa argumentada porque no existe en el estado')
        }

        state.rates.splice(index, 1)
    },

    [MutationTypes.UpdateCurrentRateField]: (state, payload: { field: string, value: string }) => {
        state.currentRate = _.set(state.currentRate, payload.field, payload.value)
    },

    /**
     * Actualizar región
     *
     * Busca, en la lista de regiones según el id temporal y sustituye la
     * coincidencia por el la región argumentada
     */
    [MutationTypes.UpdateRegion]: (state, region: Region) => {
        const index = findRegionIndex(state, region)
        if (index < 0) {
            throw new Error(`La región con id temporal ${region.tempId} no existe en el estado`)
        }

        state.regions.splice(index, 1, region)
    },
    /**
     * Actualizar regiones
     *
     * Se actualizan todas las regiones.
     */
    [MutationTypes.UpdateRegions]: (state, regions: Region[]) => {
        state.regions = regions
    },
    /**
     * Actualiza uno de los campos de la región actualmente en edición
     *
     * @param state
     * @param field
     * @param value
     */
    [MutationTypes.UpdateRegionField]: (state, {field, value}) => {
        _.set(state.currentRegion, field, value)
    },

    [MutationTypes.SetRateDetailMode]: (state, mode: RateDetailMode) => state.rateDetailMode = mode,

    /**
     * Reemplaza las regiones por unas nuevas
     */
    [MutationTypes.ReplaceRegions]: (state, payload: { regions: Region[], rate: Rate }) => {
        const filtered = state.regions.filter(r => r.rate.id !== payload.rate.id)

        state.regions = filtered.concat(payload.regions)
    },

    /**
     * Elimina una región asociada a una tarifa
     */
    [MutationTypes.RemoveRateRegion]: (state, payload: Region) => {
        const index = findRegionIndex(state, payload)
        if (index === -1) {
            throw new Error(`No existe la región con id temporal ${payload.tempId}`)
        }

        state.regions.splice(index, 1)
    },

    /**
     * Elimina de todas las regiones declaradas en la tarifa, el peso argumentado
     */
    [MutationTypes.RemoveWeightInAllRegions]: (state, weight: number) => {
        const regions = state.regions.filter(r => r.rate.tempId === state.currentRate.tempId)
        regions.forEach(r => {
            r.removeWeight(weight)
        })
    },

    /**
     * Establece el transportista actualmente en edición
     *
     * @param state
     * @param carrier
     */
    [MutationTypes.SetCurrentCarrier]: (state, carrier: Carrier) => {
        state.currentCarrier = carrier
    },
    /**
     * Actualiza un campo del transportista actualmente en edición
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateCarrierField]: (state, payload: { field: string, value: any }) => {
        _.set(state.currentCarrier, payload.field, payload.value)
    },

    /**
     * Añade un transportista a la lista, si ya existe sobre-escribirá el valor actual por
     * el nuevo
     *
     * @param state
     * @param carrier
     */
    [MutationTypes.AddCarrier]: (state, carrier: Carrier) => {
        const index = state.carriers.findIndex(c => c.id === carrier.id)
        index === -1
            ? state.carriers.push(carrier)
            : state.carriers.splice(index, 1, carrier)
    },

    /**
     * Elimina un transportista de la lista
     *
     * @param state
     * @param carrier
     */
    [MutationTypes.DeleteCarrier]: (state, carrier: number) => {
        const index = state.carriers.findIndex(c => c.id === carrier)
        if (index === -1) {
            return
        }

        state.carriers.splice(index, 1)
    },
}

export default mutations
